import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { loadTour } from '../../tours/loadTour';
import { normalizeTour, validateTour } from '../../tours/tourSchema';
import TourRunner from './TourRunner';

/**
 * THE ONE PLACE A TOUR STARTS (KPR-611). Mounted once, in AppRoot, and inert
 * until something calls `start`:
 *
 *   const { start, stop, active } = useTour();
 *   start('welcome', {
 *     context: { canAddItems },        // handed to every step's condition
 *     onComplete: (result) => save(result),
 *     onDismiss: (result) => save(result),
 *   });
 *
 * `start` takes a tour id (read through the loader) or, for a caller that
 * already holds a definition, the definition itself. It answers whether a tour
 * began: false for an id the loader does not know or a definition that does
 * not validate. One tour runs at a time; starting another ends the first as
 * `stopped`. The provider remembers nothing: progress is the caller's, saved
 * from the callbacks (see `useTourRunner` for what they are told).
 */
const TourContext = createContext(null);

const NOT_MOUNTED = Object.freeze({
	start: () => false,
	stop: () => {},
	active: null,
});

/** The tour API. Outside a provider it starts nothing, so a page can offer a tour in a test. */
export function useTour() {
	return useContext(TourContext) || NOT_MOUNTED;
}

let runs = 0;

export default function TourProvider({ children }) {
	const [run, setRun] = useState(null);
	const runRef = useRef(null);

	const clear = useCallback((key) => {
		if (runRef.current && runRef.current.key === key) {
			runRef.current = null;
			setRun(null);
		}
	}, []);

	const start = useCallback((source, options = {}) => {
		const tour = typeof source === 'string' ? loadTour(source) : (validateTour(source).length === 0 ? normalizeTour(source) : null);
		if (!tour) return false;
		// Focus goes back to where it was before the FIRST tour, not to
		// something a replaced tour's card held.
		const previous = runRef.current;
		const focusReturn = { element: typeof document === 'undefined' ? null : document.activeElement, active: true };
		if (previous) {
			focusReturn.element = previous.focusReturn.element;
			previous.focusReturn.active = false;
		}
		runs += 1;
		const key = runs;
		const callbacks = ['onStep', 'onComplete', 'onDismiss'].reduce((all, name) => ({ ...all, [name]: options[name] }), {});
		const next = {
			key,
			tour,
			focusReturn,
			context: options.context,
			timing: options.timing,
			...callbacks,
			onExit: (result) => {
				if (typeof options.onExit === 'function') options.onExit(result);
				clear(key);
			},
		};
		runRef.current = next;
		setRun(next);
		return true;
	}, [clear]);

	const stop = useCallback(() => {
		// Unmounting the runner ends it as stopped, and `onExit` clears the run.
		const current = runRef.current;
		if (!current) return;
		runRef.current = null;
		setRun(null);
	}, []);

	const value = useMemo(() => ({
		start,
		stop,
		active: run ? { id: run.tour.id, version: run.tour.version } : null,
	}), [run, start, stop]);

	return (
		<TourContext.Provider value={value}>
			{children}
			{run && (
				<TourRunner
					key={run.key}
					tour={run.tour}
					context={run.context}
					timing={run.timing}
					focusReturn={run.focusReturn}
					onStep={run.onStep}
					onComplete={run.onComplete}
					onDismiss={run.onDismiss}
					onExit={run.onExit}
				/>
			)}
		</TourContext.Provider>
	);
}
